import { useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  NOTE_REPORT_RANGES,
  filterReportRows,
  supervisionSummary,
  timelinessSummary,
  timelinessTrend,
  type NoteReportRange,
  type NoteReportView,
} from '@ehr/shared';
import { api, ApiError } from '../lib/api.js';
import {
  CHART,
  chartPoints,
  chartY,
  moreLabel,
  sessionDay,
  signedStamp,
} from '../lib/noteReports.js';
import { Modal } from './Modal.js';

/**
 * ehr#682 (PROGRESS_NOTE_10-20-V141 V123 / V124 / V133, gap #43): the Note settings footer's
 * Sign timeliness and Supervision windows. Both read GET /progress-notes/reports for the range
 * picked (who sees what is the server's: admins and managers everyone, a supervisor their
 * supervisees, anyone else their own notes) and filter it with Search users or clients.
 */

type Kind = 'timeliness' | 'supervision';

function useReport(range: NoteReportRange) {
  const [data, setData] = useState<NoteReportView | null>(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    let live = true;
    setErr('');
    api
      .get<NoteReportView>(`/progress-notes/reports?range=${range}`)
      .then((v) => live && setData(v))
      .catch(
        (e: unknown) =>
          live &&
          setErr(e instanceof ApiError ? e.message : 'Could not load the report. Try again.'),
      );
    return () => {
      live = false;
    };
  }, [range]);
  return { data: data && data.range === range ? data : null, err };
}

const Tile = ({ label, value, test }: { label: string; value: ReactNode; test?: string }) => (
  <div className="stat pn-rpt-tile">
    <span className="stat-label">{label}</span>
    <b className="stat-value" data-test={test}>
      {value}
    </b>
  </div>
);
const orNone = (v: number | null, suffix = '') => (v === null ? 'None yet' : `${v}${suffix}`);

function TimelinessChart({ rows, weekly }: { rows: NoteReportView['rows']; weekly: boolean }) {
  const pts = chartPoints(timelinessTrend(rows, weekly));
  if (!pts.length) return <p className="pn-rpt-muted">Nothing signed in this range.</p>;
  const { W, H, L, R } = CHART;
  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      role="img"
      aria-label="Percent of notes signed on time over time"
      className="pn-rpt-svg"
    >
      {[0, 50, 100].map((v) => (
        <g key={v}>
          <line x1={L} x2={W - R} y1={chartY(v)} y2={chartY(v)} className="pn-rpt-grid" />
          <text x={L - 6} y={chartY(v) + 4} textAnchor="end" className="pn-rpt-axis">
            {v}%
          </text>
        </g>
      ))}
      <polyline
        className="pn-rpt-line"
        fill="none"
        points={pts.map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ')}
      />
      {pts.map((p, i) => (
        <g key={p.key}>
          <circle cx={p.x} cy={p.y} r={4} className="pn-rpt-dot">
            <title>{`${p.label}: ${p.pct}% on time (${p.signed} signed)`}</title>
          </circle>
          {p.labelled && (
            <text
              x={p.x}
              y={H - 8}
              textAnchor={i === pts.length - 1 && pts.length > 1 ? 'end' : 'middle'}
              className="pn-rpt-axis"
            >
              {p.label}
            </text>
          )}
        </g>
      ))}
    </svg>
  );
}

function TimelinessBody({ view, q }: { view: NoteReportView; q: string }) {
  const [more, setMore] = useState(25);
  useEffect(() => setMore(25), [q, view]);
  const rows = useMemo(() => filterReportRows(view.rows, q), [view, q]);
  const s = timelinessSummary(rows);
  const weekly = view.range === '30d' || view.range === '90d';
  const shown = rows.slice(0, more);
  const approx = shown.some((r) => r.sessionEndApprox);
  return (
    <>
      <div className="pn-rpt-tiles" data-test="tl-tiles">
        <Tile label="Signed on time" value={orNone(s.pct, '%')} test="tl-pct" />
        <Tile label="Notes signed" value={s.signed} />
        <Tile label="Signed late" value={s.late} />
        <Tile label="Average hours to sign" value={orNone(s.avgHours)} />
      </div>
      <div className="pn-rpt-chart">
        <div className="pn-rpt-h pn-rpt-h0">
          Percent signed on time, {weekly ? 'by week' : 'by month'}
        </div>
        <TimelinessChart rows={rows} weekly={weekly} />
      </div>
      <p className="pn-rpt-muted">
        On time is signed within {view.lateEntryHours} hours of the session&rsquo;s end (Late entry
        in Data &amp; settings).
      </p>
      {view.seesTeam && s.byUser.length > 0 && (
        <>
          <div className="pn-rpt-h">By user</div>
          <div className="pn-rpt-wrap">
            <table className="data-table" data-test="tl-users">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Notes signed</th>
                  <th>On time</th>
                  <th>Late</th>
                  <th>Percent on time</th>
                </tr>
              </thead>
              <tbody>
                {s.byUser.map((u) => (
                  <tr key={u.authorId}>
                    <td>{u.name}</td>
                    <td>{u.signed}</td>
                    <td>{u.onTime}</td>
                    <td>{u.late}</td>
                    <td>
                      <b>{u.pct}%</b>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
      {rows.length ? (
        <>
          <div className="pn-rpt-h">Signed notes</div>
          <div className="pn-rpt-wrap">
            <table className="data-table" data-test="tl-list">
              <thead>
                <tr>
                  <th>Session</th>
                  <th>Client</th>
                  <th>User</th>
                  <th>Signed</th>
                  <th>Hours after session</th>
                  <th>On time or late</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((r) => (
                  <tr key={r.noteId}>
                    <td>{sessionDay(r.sessionDate)}</td>
                    <td>{r.clientName}</td>
                    <td>{r.authorName}</td>
                    <td>{signedStamp(r.signedAt)}</td>
                    <td>
                      {r.hoursAfter}
                      {r.sessionEndApprox ? '*' : ''}
                    </td>
                    <td>{r.late ? <span className="pn-rpt-late">Late</span> : 'On time'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {approx && (
            <p className="pn-rpt-muted">
              * The note has no end time, so the session&rsquo;s end is taken from its start and
              length.
            </p>
          )}
          {rows.length > more && (
            <button type="button" className="pn-link" onClick={() => setMore((m) => m + 25)}>
              {moreLabel(rows.length - more)}
            </button>
          )}
        </>
      ) : (
        <p className="pn-rpt-muted">No signed notes match.</p>
      )}
    </>
  );
}

function SupervisionBody({ view, q }: { view: NoteReportView; q: string }) {
  const [more, setMore] = useState(25);
  useEffect(() => setMore(25), [q, view]);
  const rows = useMemo(() => filterReportRows(view.rows, q), [view, q]);
  const s = supervisionSummary(rows);
  const shown = rows.slice(0, more);
  return (
    <>
      <div className="pn-rpt-tiles" data-test="sv-tiles">
        <Tile label="Supervised" value={orNone(s.pct, '%')} test="sv-pct" />
        <Tile label="Notes signed" value={s.signed} />
        <Tile label="Co-signed" value={s.cosigned} />
        <Tile label="Average hours to co-sign" value={orNone(s.avgHoursToCosign)} />
      </div>
      <p className="pn-rpt-muted">
        Supervised is the share of notes sent to a supervisor that were co-signed.
        {s.waiting ? ` ${s.waiting} waiting for co-sign.` : ''}
      </p>
      {view.seesTeam && s.byUser.length > 0 && (
        <>
          <div className="pn-rpt-h">By user</div>
          <div className="pn-rpt-wrap">
            <table className="data-table" data-test="sv-users">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Notes signed</th>
                  <th>Sent for co-sign</th>
                  <th>Supervised</th>
                  <th>Percent supervised</th>
                  <th>Average hours to co-sign</th>
                </tr>
              </thead>
              <tbody>
                {s.byUser.map((u) => (
                  <tr key={u.authorId}>
                    <td>{u.name}</td>
                    <td>{u.signed}</td>
                    <td>{u.needsCosign}</td>
                    <td>{u.cosigned}</td>
                    <td>{u.pct === null ? '-' : `${u.pct}%`}</td>
                    <td>{u.avgHoursToCosign ?? '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
      {view.seesTeam && s.bySupervisor.length > 0 && (
        <>
          <div className="pn-rpt-h">By supervisor</div>
          <div className="pn-rpt-wrap">
            <table className="data-table" data-test="sv-sups">
              <thead>
                <tr>
                  <th>Supervisor</th>
                  <th>Co-signed</th>
                  <th>Average hours to co-sign</th>
                </tr>
              </thead>
              <tbody>
                {s.bySupervisor.map((u) => (
                  <tr key={u.name}>
                    <td>{u.name}</td>
                    <td>{u.cosigned}</td>
                    <td>{u.avgHoursToCosign ?? '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
      {rows.length ? (
        <>
          <div className="pn-rpt-h">Signed notes</div>
          <div className="pn-rpt-wrap">
            <table className="data-table" data-test="sv-list">
              <thead>
                <tr>
                  <th>Session</th>
                  <th>Client</th>
                  <th>User</th>
                  <th>Supervised</th>
                  <th>Co-signed by</th>
                  <th>Hours to co-sign</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((r) => (
                  <tr key={r.noteId}>
                    <td>{sessionDay(r.sessionDate)}</td>
                    <td>{r.clientName}</td>
                    <td>{r.authorName}</td>
                    <td>
                      {r.cosignedAt
                        ? 'Yes'
                        : r.needsCosign
                          ? 'Waiting for co-sign'
                          : 'No co-sign needed'}
                    </td>
                    <td>{r.cosignerName ?? '-'}</td>
                    <td>{r.hoursToCosign ?? '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {rows.length > more && (
            <button type="button" className="pn-link" onClick={() => setMore((m) => m + 25)}>
              {moreLabel(rows.length - more)}
            </button>
          )}
        </>
      ) : (
        <p className="pn-rpt-muted">No signed notes in this range.</p>
      )}
    </>
  );
}

/** V141 openTimeliness / openSupervision: search, the range buttons, the report, Close. */
export function NoteReportWindow({ kind, onClose }: { kind: Kind; onClose: () => void }) {
  const [range, setRange] = useState<NoteReportRange>('6m');
  const [q, setQ] = useState('');
  const { data, err } = useReport(range);
  return (
    <Modal
      title={kind === 'timeliness' ? 'Sign timeliness' : 'Supervision'}
      onClose={onClose}
      className="pn-rpt-modal"
      actions={
        <button type="button" className="btn primary" onClick={onClose}>
          Close
        </button>
      }
    >
      <div className="pn-rpt-bar">
        <input
          className="input"
          type="search"
          placeholder="Search users or clients"
          aria-label="Search users or clients"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <div className="pn-seg" role="group" aria-label="Range">
          {NOTE_REPORT_RANGES.map(([k, label]) => (
            <button key={k} type="button" aria-pressed={range === k} onClick={() => setRange(k)}>
              {label}
            </button>
          ))}
        </div>
      </div>
      <div className="pn-rpt-out">
        {err ? (
          <p className="pn-rpt-err" role="alert">
            {err}
          </p>
        ) : !data ? (
          <p className="pn-rpt-muted">Loading…</p>
        ) : kind === 'timeliness' ? (
          <TimelinessBody view={data} q={q} />
        ) : (
          <SupervisionBody view={data} q={q} />
        )}
      </div>
    </Modal>
  );
}
